<template>
    <article class="enhanced-resume">
        <!-- Modern Header -->
        <div class="section-header">
            <h2 class="section-title">Professional History</h2>
            <div class="section-divider">
                <span class="divider-dot"></span>
                <span class="divider-line"></span>
                <span class="divider-dot"></span>
            </div>
            <p class="section-description">My journey through software development and engineering</p>
        </div>



        <!-- Experience Section -->
        <section class="timeline-section">
            <div class="section-heading">
                <div class="icon-wrapper">
                    <ion-icon name="briefcase-outline" class="section-icon"></ion-icon>
                </div>
                <h3 class="subsection-title">Professional Experience</h3>
                <div class="experience-summary">
                    <div class="years-badge">
                        <div class="years-content">
                            <span class="years-number">{{ calculateTotalExperience() }}+</span>
                            <span class="years-label">Years</span>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="timeline-container">
                <div class="timeline-line"></div>
                <div class="experience-cards">
                    <div class="experience-card" v-for="(exp, index) in experienceData" :key="index"
                         :class="{ 'featured': index === 0 }"
                         :style="{ animationDelay: `${index * 0.2}s` }">
                        
                        <div class="timeline-dot" :class="{ 'current': index === 0 }">
                            <div class="dot-inner"></div>
                        </div>
                        
                        <div class="card-content">
                            <div class="card-header">
                                <div class="company-section">
                                    <div class="company-logo">
                                        <img :src="getImagePathCompany(exp.logo)" 
                                             :alt="exp.company + ' logo'"
                                             loading="lazy">
                                    </div>
                                    <div class="company-details">
                                        <h4 class="company-name">{{ exp.company }}</h4>
                                        <span class="position-title">{{ exp.position }}</span>
                                        <div class="tech-stack" v-if="getTechStack(exp)">
                                            <span class="tech-label">Technologies:</span>
                                            <div class="tech-tags">
                                                <span v-for="tech in getTechStack(exp)" :key="tech" class="tech-tag">
                                                    {{ tech }}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="duration-info">
                                    <div class="duration-badge">
                                        <ion-icon name="calendar-outline" class="calendar-icon"></ion-icon>
                                        <span class="duration-text">{{ exp.duration }}</span>
                                    </div>
                                    <div class="duration-length" :title="calculateDuration(exp.duration).full">
                                        <div class="duration-compact">
                                            <span class="duration-display">{{ calculateDuration(exp.duration).display }}</span>
                                            <span class="duration-label">duration</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="responsibilities-section">
                                <h5 class="responsibilities-title">
                                    <ion-icon name="checkmark-circle-outline" class="title-icon"></ion-icon>
                                    Key Responsibilities & Achievements
                                </h5>
                                <div class="responsibilities-grid">
                                    <div v-for="(responsibility, idx) in getFormattedResponsibilities(exp.responsibilities)" 
                                         :key="idx"
                                         class="responsibility-card"
                                         :style="{ animationDelay: `${(index * 0.2) + (idx * 0.1)}s` }">
                                        <div class="responsibility-number">{{ idx + 1 }}</div>
                                        <div class="responsibility-content">
                                            <p class="responsibility-text">{{ responsibility }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="card-footer">
                                <a :href="exp.link" target="_blank" class="company-link" rel="noopener noreferrer">
                                    <span>Visit Company</span>
                                    <ion-icon name="open-outline"></ion-icon>
                                </a>
                                <div class="card-actions">
                                    <button class="action-btn" @click="toggleDetails(index)" 
                                            :aria-label="showDetails[index] ? 'Hide details' : 'Show details'">
                                        <ion-icon :name="showDetails[index] ? 'chevron-up-outline' : 'chevron-down-outline'"></ion-icon>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Education Section -->
        <section class="timeline-section">
            <div class="section-heading education-heading">
                <div class="icon-wrapper">
                    <ion-icon name="school-outline" class="section-icon"></ion-icon>
                </div>
                <h3 class="subsection-title">Education</h3>
            </div>
            
            <div class="education-grid">
                <div class="education-card" v-for="(edu, index) in educationData" :key="index"
                     :style="{ animationDelay: `${index * 0.3}s` }">
                    <div class="education-header">
                        <div class="institution-logo">
                            <img :src="getImagePathCompany(edu.logo)" 
                                 :alt="edu.place + ' logo'"
                                 loading="lazy">
                        </div>
                        <div class="institution-details">
                            <h4 class="institution-name">{{ edu.place }}</h4>
                            <span class="course-name">{{ edu.course }}</span>
                            <div class="education-meta">
                                <span class="education-duration">
                                    <ion-icon name="calendar-outline"></ion-icon>
                                    {{ edu.duration }}
                                </span>
                                <span class="education-years">{{ calculateEducationDuration(edu.duration) }}</span>
                            </div>
                        </div>
                    </div>
                    
                    <p class="education-description">{{ edu.description }}</p>
                    
                    <a :href="edu.link" target="_blank" class="institution-link" rel="noopener noreferrer">
                        <span>Visit Institution</span>
                        <ion-icon name="open-outline"></ion-icon>
                    </a>
                </div>
            </div>
        </section>

        <!-- Floating particles for visual appeal -->
        <div class="floating-particles">
            <div v-for="i in 6" :key="i" class="particle" :style="getParticleStyle(i)"></div>
        </div>
    </article>
</template>

<script>
import { reactive } from 'vue';
import { getImagePathCompany } from '@/utils/images/imageLoader.js';

export default {
    name: 'ProfessionalHistory',
    props: {
        experienceData: Array,
        educationData: Array
    },
    data() {
        return {
            showDetails: reactive({})
        };
    },
    methods: {
        getImagePathCompany,
        
        getParticleStyle(index) {
            const positions = [
                { top: '10%', left: '5%', animationDelay: '0s' },
                { top: '35%', right: '8%', animationDelay: '3s' },
                { top: '60%', left: '3%', animationDelay: '6s' },
                { top: '85%', right: '15%', animationDelay: '2s' },
                { top: '25%', left: '90%', animationDelay: '4s' },
                { top: '70%', right: '85%', animationDelay: '1s' }
            ];
            return positions[index - 1] || {};
        },
        
        calculateTotalExperience() {
            // Calculate total years from experience data
            let totalMonths = 0;
            this.experienceData?.forEach(exp => {
                totalMonths += this.parseDurationToMonths(exp.duration);
            });
            const years = Math.floor(totalMonths / 12);
            return years > 0 ? years : '7+'; // Fallback based on the provided data
        },
        
        calculateDuration(duration) {
            const months = this.parseDurationToMonths(duration);
            const years = Math.floor(months / 12);
            const remainingMonths = months % 12;
            
            if (years > 0 && remainingMonths > 0) {
                return {
                    display: `${years}y ${remainingMonths}m`,
                    full: `${years} year${years > 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`,
                    years: years,
                    months: remainingMonths
                };
            } else if (years > 0) {
                return {
                    display: `${years}y`,
                    full: `${years} year${years > 1 ? 's' : ''}`,
                    years: years,
                    months: 0
                };
            } else {
                return {
                    display: `${remainingMonths}m`,
                    full: `${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`,
                    years: 0,
                    months: remainingMonths
                };
            }
        },
        
        calculateEducationDuration(duration) {
            const years = duration.match(/(\d{4})\s*-\s*(\d{4})/);
            if (years) {
                const startYear = parseInt(years[1]);
                const endYear = parseInt(years[2]);
                const diff = endYear - startYear;
                return `${diff} year${diff > 1 ? 's' : ''}`;
            }
            return '';
        },
        
        parseDurationToMonths(duration) {
            // Parse duration like "Apr 2022 — Present" or "Feb 2018 - Mar 2022"
            const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                          'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            
            const parts = duration.toLowerCase().replace(/—/g, '-').split(' - ');
            if (parts.length !== 2) return 0;
            
            const start = parts[0].trim();
            const end = parts[1].trim();
            
            const parseDate = (dateStr) => {
                const parts = dateStr.split(' ');
                if (parts.length !== 2) return null;
                const monthIndex = months.findIndex(m => parts[0].startsWith(m.substring(0, 3)));
                const year = parseInt(parts[1]);
                return { month: monthIndex, year };
            };
            
            const startDate = parseDate(start);
            const endDate = end === 'present' ? 
                { month: new Date().getMonth(), year: new Date().getFullYear() } : 
                parseDate(end);
            
            if (!startDate || !endDate) return 0;
            
            return (endDate.year - startDate.year) * 12 + (endDate.month - startDate.month);
        },
        
        getTechStack(experience) {
            // Extract technologies from responsibilities text
            const text = experience.responsibilities.join(' ').toLowerCase();
            const technologies = [];
            
            const techKeywords = {
                'angular': 'Angular',
                'typescript': 'TypeScript', 
                'java': 'Java',
                'spring boot': 'Spring Boot',
                'flutter': 'Flutter',
                'dart': 'Dart',
                '.net': '.NET',
                'xamarin': 'Xamarin',
                'mssql': 'MSSQL',
                'azure': 'Azure',
                'mysql': 'MySQL'
            };
            
            for (const [keyword, display] of Object.entries(techKeywords)) {
                if (text.includes(keyword) && !technologies.includes(display)) {
                    technologies.push(display);
                }
            }
            
            return technologies.slice(0, 5); // Limit to 5 technologies
        },
        
        getFormattedResponsibilities(responsibilities) {
            // Remove numbering and clean up text
            return responsibilities.map(resp => {
                return resp.replace(/^\d+\s*\.\s*/, '').trim();
            });
        },
        
        toggleDetails(index) {
            this.showDetails[index] = !this.showDetails[index];
        }
    }
}
</script>

<style scoped>
.enhanced-resume {
    padding: 3rem 1rem;
    position: relative;
    overflow: hidden;
    max-width: 1200px;
    margin: 0 auto;
}

/* Section Header Styling - matching the skills section */
.section-header {
    text-align: center;
    margin-bottom: 4rem;
}

.section-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--white-1);
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.section-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1.5rem 0;
    gap: 1rem;
}

.divider-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.divider-line {
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, transparent, #667eea, transparent);
}

.section-description {
    color: var(--light-gray-70);
    font-size: 1.1rem;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
}



/* Timeline Section */
.timeline-section {
    margin-bottom: 4rem;
}

.section-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 2.5rem;
    padding-left: 1rem;
    flex-wrap: wrap;
}

.education-heading {
    justify-content: center;
    padding-left: 0;
}

.section-heading > div:first-child {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.experience-summary {
    display: flex;
    align-items: center;
}

.years-badge {
    background: linear-gradient(145deg, #e6e6e6, #c0c0c0);
    color: #333;
    border-radius: 8px;
    font-weight: 600;
    box-shadow: 
        0 2px 8px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.5);
    border: 1px solid rgba(0, 0, 0, 0.1);
    min-width: 70px;
    transition: all 0.2s ease;
}

.years-badge:hover {
    transform: translateY(-1px);
    box-shadow: 
        0 4px 12px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.years-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.5rem 0.8rem;
}

.years-number {
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1;
    color: #333;
}

.years-label {
    font-size: 0.65rem;
    font-weight: 500;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    margin-top: 0.1rem;
}

.icon-wrapper {
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.section-icon {
    font-size: 1.5rem;
    color: white;
}

.subsection-title {
    font-size: 1.8rem;
    font-weight: 600;
    color: var(--white-1);
    margin: 0;
}

/* Timeline Container */
.timeline-container {
    position: relative;
    padding-left: 2rem;
}

.timeline-line {
    position: absolute;
    left: 2.5rem;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
    border-radius: 2px;
    box-shadow: 0 0 10px rgba(102, 126, 234, 0.3);
}

.experience-cards {
    position: relative;
}

/* Experience Cards */
.experience-card {
    position: relative;
    margin-bottom: 3rem;
    margin-left: 3rem;
    opacity: 0;
    animation: slideInCard 0.6s ease-out forwards;
}

@keyframes slideInCard {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.timeline-dot {
    position: absolute;
    left: -4.25rem;
    top: 1.5rem;
    width: 16px;
    height: 16px;
    background: var(--smoky-black-3);
    border: 4px solid #667eea;
    border-radius: 50%;
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
}

.dot-inner {
    width: 6px;
    height: 6px;
    background: #667eea;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.timeline-dot.current {
    background: #667eea;
    border-color: #764ba2;
    box-shadow: 0 0 30px rgba(102, 126, 234, 0.8);
    animation: pulse-dot 2s infinite;
}

.timeline-dot.current .dot-inner {
    opacity: 1;
    background: white;
}

.card-content {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    padding: 2rem;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.card-content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
}

.experience-card:hover .card-content {
    transform: translateY(-5px);
    border-color: rgba(102, 126, 234, 0.3);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.experience-card:hover .card-content::before {
    opacity: 1;
}

.experience-card.featured .card-content {
    border-color: rgba(102, 126, 234, 0.3);
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
}

/* Card Header */
.card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
    gap: 1rem;
}

.company-section {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    flex: 1;
}

.company-logo {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
}

.company-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.company-details h4.company-name {
    font-size: 1.3rem;
    font-weight: 600;
    color: var(--white-1);
    margin: 0 0 0.5rem 0;
    line-height: 1.2;
}

.position-title {
    color: #667eea;
    font-weight: 500;
    font-size: 1rem;
    display: block;
    margin-bottom: 0.8rem;
}

.tech-stack {
    margin-top: 0.8rem;
}

.tech-label {
    color: var(--light-gray-70);
    font-size: 0.85rem;
    font-weight: 500;
    display: block;
    margin-bottom: 0.5rem;
}

.tech-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.tech-tag {
    background: rgba(102, 126, 234, 0.2);
    border: 1px solid rgba(102, 126, 234, 0.3);
    color: var(--white-2);
    padding: 0.2rem 0.6rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
}

.duration-info {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
}

.duration-badge {
    background: rgba(102, 126, 234, 0.2);
    border: 1px solid rgba(102, 126, 234, 0.3);
    border-radius: 25px;
    padding: 0.5rem 1rem;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.calendar-icon {
    font-size: 0.9rem;
    color: #667eea;
}

.duration-text {
    color: var(--white-2);
    font-size: 0.9rem;
    font-weight: 500;
}

.duration-length {
    background: rgba(102, 126, 234, 0.1);
    border: 1px solid rgba(102, 126, 234, 0.2);
    border-radius: 12px;
    padding: 0.4rem 0.8rem;
    transition: all 0.3s ease;
}

.duration-length:hover {
    background: rgba(102, 126, 234, 0.15);
    border-color: rgba(102, 126, 234, 0.3);
    transform: scale(1.05);
}

.duration-compact {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.1rem;
}

.duration-display {
    color: var(--white-1);
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.3px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.duration-label {
    color: var(--light-gray-70);
    font-size: 0.65rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.8;
}

/* Responsibilities Section */
.responsibilities-section {
    margin-bottom: 1.5rem;
}

.responsibilities-title {
    color: var(--white-1);
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.title-icon {
    color: #667eea;
    font-size: 1.2rem;
}

.responsibilities-grid {
    display: grid;
    gap: 1rem;
}

.responsibility-card {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 12px;
    padding: 1rem;
    display: flex;
    gap: 1rem;
    transition: all 0.3s ease;
    opacity: 0;
    animation: slideInResponsibility 0.5s ease-out forwards;
}

@keyframes slideInResponsibility {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.responsibility-card:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(102, 126, 234, 0.2);
    transform: translateX(5px);
}

.responsibility-number {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    font-weight: 600;
    flex-shrink: 0;
    margin-top: 0.1rem;
}

.responsibility-content {
    flex: 1;
}

.responsibility-text {
    color: var(--light-gray-70);
    line-height: 1.6;
    margin: 0;
    font-size: 0.95rem;
}

.card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* Company Link */
.company-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #667eea;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.3s ease;
    position: relative;
    z-index: 1;
}

.company-link:hover {
    color: #764ba2;
    transform: translateX(5px);
}

.card-actions {
    display: flex;
    gap: 0.5rem;
}

.action-btn {
    background: rgba(102, 126, 234, 0.1);
    border: 1px solid rgba(102, 126, 234, 0.2);
    color: #667eea;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
}

.action-btn:hover {
    background: rgba(102, 126, 234, 0.2);
    transform: scale(1.1);
}

/* Education Section */
.education-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 2rem;
    margin: 2rem auto 0;
    padding: 0 1rem;
    max-width: 1200px;
    justify-items: center;
}

.education-card {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    padding: 2rem;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    opacity: 0;
    animation: slideInCard 0.6s ease-out forwards;
}

.education-card:hover {
    transform: translateY(-5px);
    border-color: rgba(102, 126, 234, 0.3);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.education-header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.institution-logo {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
}

.institution-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.institution-details h4.institution-name {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--white-1);
    margin: 0 0 0.5rem 0;
    line-height: 1.2;
}

.course-name {
    color: #667eea;
    font-weight: 500;
    font-size: 1rem;
    display: block;
    margin-bottom: 0.8rem;
}

.education-meta {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

.education-duration {
    color: var(--light-gray-70);
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.education-duration ion-icon {
    font-size: 0.8rem;
    color: #667eea;
}

.education-years {
    color: #667eea;
    font-size: 0.8rem;
    font-weight: 500;
}

.education-description {
    color: var(--light-gray-70);
    line-height: 1.6;
    margin-bottom: 1.5rem;
}

.institution-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #667eea;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.3s ease;
}

.institution-link:hover {
    color: #764ba2;
    transform: translateX(5px);
}

/* Floating Particles */
.floating-particles {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: -1;
}

.particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    opacity: 0.6;
    animation: float 8s ease-in-out infinite;
}

/* Animations */
@keyframes pulse-dot {
    0%, 100% {
        transform: scale(1);
        box-shadow: 0 0 20px rgba(102, 126, 234, 0.5);
    }
    50% {
        transform: scale(1.2);
        box-shadow: 0 0 30px rgba(102, 126, 234, 0.8);
    }
}

@keyframes float {
    0%, 100% {
        transform: translateY(0px) rotate(0deg);
    }
    33% {
        transform: translateY(-20px) rotate(120deg);
    }
    66% {
        transform: translateY(10px) rotate(240deg);
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .enhanced-resume {
        padding: 2rem 1rem;
    }

    .section-title {
        font-size: 2rem;
    }

    .section-heading {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .section-heading > div:first-child {
        width: 100%;
    }

    .timeline-container {
        padding-left: 1rem;
    }

    .timeline-line {
        left: 1.5rem;
    }

    .experience-card {
        margin-left: 2rem;
    }

    .timeline-dot {
        left: -3.25rem;
    }

    .card-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .company-section {
        width: 100%;
    }

    .duration-info {
        align-items: flex-start;
        width: 100%;
    }

    .duration-badge {
        align-self: flex-start;
    }
    
    .duration-length {
        align-self: flex-start;
        padding: 0.25rem 0.5rem;
    }
    
    .duration-display {
        font-size: 0.75rem;
    }
    
    .duration-label {
        font-size: 0.55rem;
    }

    .tech-tags {
        justify-content: flex-start;
    }

    .responsibilities-grid {
        gap: 0.8rem;
    }

    .responsibility-card {
        padding: 0.8rem;
    }

    .card-footer {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }

    .education-grid {
        grid-template-columns: 1fr;
        gap: 1.5rem;
        padding: 0 0.5rem;
    }

    .education-header {
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
    }
}

@media (max-width: 480px) {
    .enhanced-resume {
        padding: 1.5rem 0.5rem;
    }

    .card-content {
        padding: 1.5rem;
    }

    .education-card {
        padding: 1.5rem;
    }

    .education-grid {
        padding: 0;
        gap: 1rem;
    }

    .company-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.8rem;
    }

    .company-logo, .institution-logo {
        width: 50px;
        height: 50px;
    }

    .responsibility-card {
        flex-direction: column;
        gap: 0.5rem;
        padding: 1rem;
    }

    .responsibility-number {
        align-self: flex-start;
    }

    .tech-tags {
        gap: 0.3rem;
    }

    .tech-tag {
        font-size: 0.7rem;
        padding: 0.15rem 0.5rem;
    }

    .years-badge {
        min-width: 100px;
    }
    
    .years-content {
        padding: 0.8rem 1.2rem;
    }
    
    .years-number {
        font-size: 1.6rem;
    }
    
    .years-plus {
        font-size: 1rem;
    }
    
    .years-label {
        font-size: 0.65rem;
        letter-spacing: 0.8px;
    }
    
    .years-sparkle {
        font-size: 0.75rem;
        top: 0.4rem;
        right: 0.4rem;
    }
    
    .years-orb-1 {
        width: 10px;
        height: 10px;
    }
    
    .years-orb-2 {
        width: 7px;
        height: 7px;
    }
    
    .years-orb-3 {
        width: 5px;
        height: 5px;
    }
}

/* Fix for larger screens to ensure proper width constraints */
.enhanced-resume {
    max-width: 100%;
    overflow-x: hidden; /* Prevent horizontal scrolling */
    box-sizing: border-box;
}

.timeline-container {
    max-width: 100%;
    overflow-x: hidden;
}

.experience-card {
    max-width: 100%;
    box-sizing: border-box;
}

/* Ensure images don't overflow on larger screens */
img {
    max-width: 100%;
}
</style>
